import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronRight, Copy, FileText, Loader2, MessageSquare, MoreHorizontal, Paperclip, Pencil, Pin, PinOff, Plus, Search, Square, Trash2, X } from 'lucide-react';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import { usesChatFree } from '../utils/moduleAccess';
import ChatMessageMarkdown from '../components/chat/ChatMessageMarkdown';
import LayeredAnswer from '../components/chat/LayeredAnswer';
import AnswerConcepts from '../components/chat/AnswerConcepts';
import type { ConceptTerm } from '../lookupApi';
import { DefinitionPreviewProvider } from '../components/chat/ConceptLink';
import { LibraryPanelHost } from '../components/chat/LibraryPanel';
import MessageSourceIcons from '../components/chat/MessageSourceIcons';
import api, { generateProjectTitle, type MessageSourcesUsed } from '../apiService';
import { formatDateTime } from '../utils/formatDateTime';
import { LOOKUP_TOOL_DESCRIPTIONS } from '../components/chat/lookupToolDescriptions';
import { useAnswerLinkClicks } from '../components/chat/answerLinkClicks';
import { useFollowLatest } from '../hooks/useFollowLatest';
import { ExpandAllBlock, LayeredAnswerLayout, LookupAttachment, LookupMessage, ReadMoreTiming, SavedLookupChat, deleteLookupChat, getLookupChat, isLookupChatFree, listLookupChats, pinLookupChat, recordExpandAll, recordLink, recordReadMore, renameLookupChat, saveLookupChat, streamLookupTurn, uploadChatFiles } from '../lookupApi';
import { CHAT_UPLOAD_ACCEPT, DEFAULT_CHAT_UPLOAD_CONFIG, formatFileSize, isSupportedUploadFile, loadChatUploadConfig } from '../components/chat/chatUploadConfig';

interface ChatMessage extends LookupMessage {
  interrupted?: boolean;
  sources?: MessageSourcesUsed;
  concepts?: ConceptTerm[];
  /** Headlines over `content` (saved with the chat); absent: shown as written. */
  layered?: LayeredAnswerLayout;
  /** The answer's `done.turn_id` (saved with the chat): opening or closing a
   *  headline is recorded under it. Absent in older chats: nothing is sent. */
  turnId?: string;
}

const newChatPath = () => `/lookup/${crypto.randomUUID()}`;
const PINNED_LABEL = 'Pinned';
const COLLAPSED_KEY = 'bark.chat.collapsedGroups';

/** When a chat was last used: "Today", "Yesterday", else the day with its year ("Oct 7, 2026"). */
function lastUsedLabel(iso: string): string {
  const day = new Date(iso);
  day.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((today.getTime() - day.getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return formatDateTime(iso, { context: 'date' });
}

interface LookupChatProps {
  chatId: string;
  token: string;
  onSaved: (chatId: string, thread: ChatMessage[]) => void;
  /** A new chat's first question was sent: list the chat before it is saved. */
  onStarted: (chatId: string, question: string) => void;
  /** Left before its first save (New chat, another chat, navigating away):
   *  the chat was never saved, so its row from `onStarted` goes. */
  onAbandoned: (chatId: string) => void;
}

/** Copies one answer's text (as written, with its markdown). */
function CopyAnswer({ text }: { text: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const reset = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(reset.current), []);
  function copy() {
    const done = (next: 'copied' | 'failed') => {
      setState(next);
      clearTimeout(reset.current);
      reset.current = setTimeout(() => setState('idle'), 2000);
    };
    if (!navigator.clipboard) { done('failed'); return; }
    navigator.clipboard.writeText(text).then(() => done('copied'), () => done('failed'));
  }
  return (
    <button type="button" onClick={copy} aria-label="Copy answer" className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs text-fg-tertiary hover:text-fg hover:bg-surface-sunken focus-visible:shadow-bark-focus">
      {state === 'copied' ? <Check size={14} strokeWidth={1.5} aria-hidden="true" /> : <Copy size={14} strokeWidth={1.5} aria-hidden="true" />}
      <span aria-live="polite">{state === 'copied' ? 'Copied' : state === 'failed' ? 'Could not copy' : 'Copy'}</span>
    </button>
  );
}

function LookupChat({ chatId, token, onSaved, onStarted, onAbandoned }: LookupChatProps) {
  // The account's first name, as the start page greets by name.
  const firstName = (useAuth().userData?.name || '').trim().split(/\s+/)[0];
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState(false);
  const active = useRef<AbortController | null>(null);
  const scroll = useRef<HTMLDivElement>(null);
  // Follows the answer to the end until the reader scrolls up.
  const { following, atLatest, pin, jump, props: followProps } = useFollowLatest(scroll);
  // An answer that finished while the reader was scrolled up keeps showing
  // as written, so nothing moves under them, until they are back at the end.
  const [heldAsWritten, setHeldAsWritten] = useState(false);
  useEffect(() => { if (atLatest) setHeldAsWritten(false); }, [atLatest]);
  const composer = useRef<HTMLTextAreaElement>(null);
  // Files picked for the next question; uploaded when it is sent.
  const [pending, setPending] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const picker = useRef<HTMLInputElement>(null);
  const [uploadConfig, setUploadConfig] = useState(DEFAULT_CHAT_UPLOAD_CONFIG);
  const auth = useAuth().userData;
  const uploader = auth?.email || auth?.uid || auth?.id || '';
  // Free-plan Chat runs on chat-free, which has no file tools.
  const canAttach = !isLookupChatFree();
  useEffect(() => { if (canAttach) void loadChatUploadConfig().then(setUploadConfig); }, [canAttach]);

  // True once this chat exists on the server (loaded with turns, or saved).
  const stored = useRef(false);
  const mounted = useRef(true);
  // True once onStarted listed this chat (its first question was sent).
  const started = useRef(false);
  const abandon = useRef(onAbandoned);
  abandon.current = onAbandoned;
  useEffect(() => () => {
    // A chat listed by onStarted but never saved (its answer was cancelled
    // here, or its first save failed) exists nowhere, so its row goes too.
    if (started.current && !stored.current) abandon.current(chatId);
    active.current?.abort();
    active.current = null;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    // Once per chat: the route remounts this component for another chat, and
    // a refreshed token must not reload the thread under an open answer. A
    // failed load keeps the composer locked, so a send cannot overwrite it.
    let current = true;
    getLookupChat<ChatMessage>(chatId, token).then(saved => {
      if (!current) return;
      if (saved) { setMessages(saved); stored.current = true; }
      setLoading(false);
    }, () => { if (current) setLoadError(true); });
    return () => { current = false; };
  }, [chatId, loadAttempt]); // eslint-disable-line react-hooks/exhaustive-deps
  // Ready to type as soon as the chat has loaded (also from the start page's
  // "Start new chat").
  useEffect(() => { if (!loading) composer.current?.focus(); }, [loading]);
  useEffect(pin, [messages, status, pin]);

  // Opening or closing a headline is recorded for analytics only, never
  // awaited and never shown; an answer without a turn id records nothing.
  // A close carries its timing.
  const readMoreRecorder = (turnId?: string) => turnId
    ? (block: number, open: boolean, timing?: ReadMoreTiming) => {
        (timing ? recordReadMore(turnId, block, open, token, timing) : recordReadMore(turnId, block, open, token)).catch(() => undefined);
      }
    : undefined;
  // So is a press of "Expand all" or "Collapse all", once per press.
  const expandAllRecorder = (turnId?: string) => turnId
    ? (open: boolean, blocks: ExpandAllBlock[]) => { recordExpandAll(turnId, open, blocks, token).catch(() => undefined); }
    : undefined;
  // So is a click on a link in an answer, and the time away at it.
  const linkClicks = useAnswerLinkClicks((turnId, block, url, awaySeconds) => {
    (awaySeconds === undefined ? recordLink(turnId, block, url, token) : recordLink(turnId, block, url, token, awaySeconds)).catch(() => undefined);
  });

  function pick(list: FileList | null) {
    const files = Array.from(list || []);
    const fits = (f: File) => isSupportedUploadFile(f, uploadConfig) && f.size <= uploadConfig.maxBytes;
    const refused = files.find(f => !fits(f));
    setFileError(refused ? `${refused.name} cannot be attached: ${isSupportedUploadFile(refused, uploadConfig) ? `it is over ${formatFileSize(uploadConfig.maxBytes)}` : 'this file type is not supported'}.` : '');
    setPending(now => [...now, ...files.filter(fits)]);
    if (picker.current) picker.current.value = '';
  }

  // The picked files are uploaded first; the question goes only once they all are.
  async function submit() {
    const question = draft.trim();
    if (!question || busy || loading || uploading) return;
    let attachments: LookupAttachment[] | undefined;
    if (pending.length) {
      setUploading(true);
      setFileError('');
      try {
        attachments = await uploadChatFiles(chatId, pending, uploader, uploadConfig.multipartMaxBytes);
      } catch (cause) {
        setFileError(cause instanceof Error ? cause.message : 'The file could not be uploaded.');
        return;
      } finally {
        setUploading(false);
      }
      // Left the chat while the files uploaded: the question is not sent.
      if (!mounted.current) return;
      setPending([]);
    }
    void send(messages, question, attachments);
  }

  async function send(history: ChatMessage[], question?: string, attachments?: LookupAttachment[]) {
    if (active.current) return;
    const asked: ChatMessage = attachments?.length ? { role: 'user', content: question || '', attachments } : { role: 'user', content: question || '' };
    const next: ChatMessage[] = question ? [...history, asked] : history;
    if (question && !history.some(m => m.role === 'user')) { started.current = true; onStarted(chatId, question); }
    const controller = new AbortController();
    active.current = controller;
    setMessages([...next, { role: 'assistant', content: '' }]);
    setDraft('');
    setBusy(true);
    setError('');
    setStatus('Thinking');
    jump();
    let answer = '';
    let sources: MessageSourcesUsed | undefined;
    let concepts: ConceptTerm[] | undefined;
    let layered: LayeredAnswerLayout | undefined;
    let turnId: string | undefined;
    let thread = next;
    const running = new Map<string, string>();
    const updateAnswer = (interrupted = false) => {
      thread = [...next, { role: 'assistant', content: answer, interrupted, sources, concepts, layered, turnId }];
      if (active.current === controller) setMessages(thread);
    };
    try {
      await streamLookupTurn(
        next.filter(m => m.content.trim()).map(({ role, content, attachments }) => (attachments?.length ? { role, content, attachments } : { role, content })),
        token, controller.signal, event => {
          if (active.current !== controller || controller.signal.aborted) return;
          if (event.event === 'token') {
            answer += event.data.text;
            updateAnswer();
            setStatus('Writing answer');
          } else if (event.event === 'done') {
            answer = event.data.text;
            sources = event.data.sources;
            concepts = event.data.concepts;
            layered = event.data.layered;
            turnId = typeof event.data.turn_id === 'string' ? event.data.turn_id : undefined;
            updateAnswer();
          } else if (event.event === 'tool_call') {
            running.set(event.data.id, LOOKUP_TOOL_DESCRIPTIONS[event.data.name] || 'Looking up sources');
            setStatus(running.values().next().value || 'Looking up sources');
          } else if (event.event === 'tool_result') {
            running.delete(event.data.id);
            setStatus(running.values().next().value || 'Thinking');
          }
        },
        chatId,
      );
    } catch (cause) {
      if (active.current !== controller) return;
      updateAnswer(true);
      if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'Chat failed. Please try again.');
    } finally {
      if (active.current === controller) {
        active.current = null;
        setBusy(false);
        setStatus('');
        setHeldAsWritten(!following.current);
        composer.current?.focus();
        // Save settled turns only, rather than the chat on every token.
        saveLookupChat(chatId, thread, token).then(() => { stored.current = true; setSaveError(false); onSaved(chatId, thread); }, () => setSaveError(true));
      }
    }
  }

  return (
    <DefinitionPreviewProvider token={token} chatId={chatId}>
    <section aria-label="Chat" className="h-full min-h-0 flex flex-col bg-surface text-fg">
      <div ref={scroll} className="flex-1 min-h-0 overflow-y-auto" {...followProps}>
        <div className="max-w-3xl mx-auto px-6 py-6 space-y-6">
          {loading && !loadError && <p role="status" className="flex items-center gap-2 text-sm text-fg-secondary"><Loader2 size={16} strokeWidth={1.5} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />Loading chat</p>}
          {loadError && <div role="alert" className="bg-danger-bg rounded-md p-3 text-sm">
            <p>This chat could not be loaded.</p>
            <button type="button" className="mt-2 underline rounded focus-visible:shadow-bark-focus" onClick={() => { setLoadError(false); setLoadAttempt(n => n + 1); }}>Try again</button>
          </div>}
          {!loading && messages.length === 0 && (
            <div className="py-12 text-center">
              <MessageSquare size={24} strokeWidth={1.5} className="mx-auto mb-4 text-fg-tertiary" aria-hidden="true" />
              <h2 className="text-lg font-semibold mb-2">{firstName ? `Hi ${firstName}, what are we looking into today?` : 'Hi, what are we looking into today?'}</h2>
              <p className="text-sm text-fg-secondary">Answers come with their sources.</p>
            </div>
          )}
          {messages.map((message, i) => {
            // The answer still being written gets its sources and Copy only
            // once it is finished: until then its sources are not known.
            const writing = busy && i === messages.length - 1;
            const asWritten = writing || (heldAsWritten && i === messages.length - 1);
            return (
            <article key={i} aria-label={message.role === 'user' ? 'Your question' : 'BARK answer'} className={message.role === 'user' ? 'bg-surface-sunken rounded-lg px-4 py-3' : 'min-w-0 break-words'} {...(message.role === 'assistant' ? linkClicks(message.turnId) : undefined)}>
              <p className="text-xs font-semibold text-fg-tertiary mb-2">{message.role === 'user' ? 'You' : 'BARK'}</p>
              {message.role === 'user' ? <>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  {!!message.attachments?.length && <ul aria-label="Attached files" className="mt-2 flex flex-wrap gap-2">
                    {message.attachments.map(file => <li key={file.upload_id} className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-border-default bg-surface px-2 py-1 text-xs text-fg-secondary"><FileText size={14} strokeWidth={1.5} aria-hidden="true" className="shrink-0" /><span className="truncate">{file.name}</span></li>)}
                  </ul>}
                </>
                : <AnswerConcepts content={message.content} concepts={message.concepts}>
                    <div className="prose-chat">
                      {message.layered && !asWritten ? <LayeredAnswer content={message.content} layered={message.layered} onToggle={readMoreRecorder(message.turnId)} onToggleAll={expandAllRecorder(message.turnId)} />
                        : <ChatMessageMarkdown content={message.content} />}
                    </div>
                  </AnswerConcepts>}
              {message.interrupted && <p className="text-xs text-fg-tertiary mt-2">Answer interrupted</p>}
              {message.role === 'assistant' && message.content && !writing && <div data-answer-chrome className="mt-3 flex items-center gap-3"><MessageSourceIcons sources={message.sources} /><CopyAnswer text={message.content} /></div>}
            </article>
            );
          })}
          {(busy || uploading) && <p role="status" className="flex items-center gap-2 text-sm text-fg-secondary"><Loader2 size={16} strokeWidth={1.5} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />{uploading ? 'Uploading files' : status}</p>}
          {error && <div role="alert" className="bg-danger-bg rounded-md p-3 text-sm">
            <p>{error}</p>
            <button type="button" className="mt-2 underline rounded focus-visible:shadow-bark-focus" onClick={() => void send(messages.slice(0, -1))}>Try again</button>
          </div>}
        </div>
      </div>
      <div className="w-full max-w-3xl mx-auto px-6 pb-4 pt-3">
        {!atLatest && messages.length > 0 && (
          <div className="flex justify-center mb-2">
            <button type="button" onClick={jump} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border-default bg-surface text-xs font-semibold text-fg-secondary shadow-bark-xs hover:text-fg focus-visible:shadow-bark-focus">
              <ArrowDown size={14} strokeWidth={1.5} aria-hidden="true" /> Jump to latest
            </button>
          </div>
        )}
        {saveError && <p role="status" className="text-xs text-fg-secondary mb-2">This chat could not be saved. Keep this page open to retain it.</p>}
        <form className="border border-border-default rounded-lg p-3 focus-within:shadow-bark-focus" onSubmit={event => {
          event.preventDefault();
          void submit();
        }}>
          {fileError && <p role="alert" className="mb-2 text-xs text-danger">{fileError}</p>}
          {pending.length > 0 && <ul aria-label="Files to attach" className="mb-2 flex flex-wrap gap-2">
            {pending.map((file, i) => <li key={`${file.name}-${i}`} className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-surface-sunken px-2 py-1 text-xs text-fg-secondary">
              <FileText size={14} strokeWidth={1.5} aria-hidden="true" className="shrink-0" /><span className="truncate">{file.name}</span>
              {!uploading && <button type="button" aria-label={`Remove ${file.name}`} onClick={() => setPending(now => now.filter((_, j) => j !== i))} className="rounded text-fg-tertiary hover:text-fg focus-visible:shadow-bark-focus"><X size={12} strokeWidth={1.5} aria-hidden="true" /></button>}
            </li>)}
          </ul>}
          <textarea ref={composer} aria-label="Your question" placeholder="Ask a question or follow up…" rows={3} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              void submit();
            }
          }} className="block w-full resize-none border-0 rounded-none bg-transparent p-0 text-sm text-fg placeholder:text-fg-tertiary focus:outline-none focus:shadow-none" />
          <div className="flex items-center justify-end gap-2">
            {canAttach && <>
              <input ref={picker} type="file" multiple hidden accept={CHAT_UPLOAD_ACCEPT} onChange={e => pick(e.target.files)} />
              <button type="button" aria-label="Attach files" title="Attach files" disabled={busy || uploading || loading} onClick={() => picker.current?.click()} className="mr-auto p-2 rounded-md text-fg-tertiary hover:text-fg hover:bg-surface-sunken disabled:opacity-40 focus-visible:shadow-bark-focus"><Paperclip size={20} strokeWidth={1.5} aria-hidden="true" /></button>
            </>}
            {busy ? <button type="button" aria-label="Stop answer" onClick={() => active.current?.abort()} className="p-2 rounded-md bg-primary text-on-primary focus-visible:shadow-bark-focus"><Square size={20} strokeWidth={1.5} aria-hidden="true" /></button>
              : <button type="submit" aria-label="Send question" disabled={!draft.trim() || uploading} className="p-2 rounded-md bg-accent-copper text-on-primary disabled:opacity-40 focus-visible:shadow-bark-focus"><ArrowUp size={20} strokeWidth={1.5} aria-hidden="true" /></button>}
          </div>
        </form>
      </div>
    </section>
    </DefinitionPreviewProvider>
  );
}

function LookupPage({ chatId, token }: { chatId: string; token: string }) {
  const navigate = useNavigate();
  // null until the first load succeeds; a failed load keeps what was shown.
  const [chats, setChats] = useState<SavedLookupChat[] | null>(null);
  const [listError, setListError] = useState(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const menu = useRef<HTMLDivElement>(null);
  const [deleting, setDeleting] = useState<SavedLookupChat | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  // Folded groups ("Pinned", "Today", "Oct 9, 2026"), remembered per browser.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '[]') as string[]); } catch { return new Set(); }
  });
  // An open options menu closes on a click outside it or on Escape.
  useEffect(() => {
    if (!menuFor) return;
    const outside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!menu.current?.contains(target) && !target.closest('[aria-haspopup="menu"]')) setMenuFor(null);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenuFor(null); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', escape); };
  }, [menuFor]);
  // Enter and the blur that follows must commit once; Escape must not commit.
  const editing = useRef<string | null>(null);
  const refresh = useCallback(() => {
    listLookupChats(token).then(all => { setChats(all); setListError(false); }, () => setListError(true));
  }, [token]);
  useEffect(refresh, [refresh]);
  // Read by the auto-title once its model call returns, after any rename meanwhile.
  const chatsNow = useRef(chats);
  chatsNow.current = chats;
  const titled = useRef(new Set<string>());
  const chatFree = usesChatFree(useAuth().userData);

  // After a chat's first answer is saved, name it by its topic, as projects are
  // named (generateProjectTitle). Best-effort: on any failure the chat keeps its
  // first question as its name. A name the person gave always wins.
  function autoTitle(savedId: string, thread: ChatMessage[]) {
    const questions = thread.filter(m => m.role === 'user');
    const answer = thread[thread.length - 1];
    if (questions.length !== 1 || answer?.role !== 'assistant' || !answer.content.trim() || titled.current.has(savedId)) return;
    titled.current.add(savedId);
    const question = questions[0].content;
    (chatFree ? generateProjectTitle(question, [], { chatFree: true }) : generateProjectTitle(question)).then(title => {
      const listed = chatsNow.current?.find(c => c.chat_id === savedId);
      if (!title || title === question || editing.current === savedId || (listed && listed.title !== question)) return;
      setChats(all => all && all.map(c => (c.chat_id === savedId ? { ...c, title } : c)));
      // Refresh either way: a list load started before the rename can return the old name.
      renameLookupChat(savedId, title, token).then(refresh, refresh);
    });
  }

  function startRename(chat: SavedLookupChat) {
    setMenuFor(null);
    editing.current = chat.chat_id;
    setRenameDraft(chat.title);
    setRenaming(chat.chat_id);
  }

  function endRename(chat: SavedLookupChat, save: boolean) {
    if (editing.current !== chat.chat_id) return;
    editing.current = null;
    setRenaming(null);
    const title = renameDraft.trim();
    if (!save || !title || title === chat.title) return;
    setChats(all => all && all.map(c => (c.chat_id === chat.chat_id ? { ...c, title } : c)));
    renameLookupChat(chat.chat_id, title, token).catch(refresh);
  }

  // A new chat shows under Today as soon as its first question is sent; the
  // list load after its first save replaces this row with the saved one.
  function listStarted(startedId: string, question: string) {
    setChats(all => (all && !all.some(c => c.chat_id === startedId)
      ? [{ chat_id: startedId, title: question, updated_at: new Date().toISOString() }, ...all]
      : all));
  }

  function unlistAbandoned(abandonedId: string) {
    setChats(all => all && all.filter(c => c.chat_id !== abandonedId));
  }

  function togglePin(chat: SavedLookupChat) {
    setMenuFor(null);
    const pinned = !chat.pinned;
    setChats(all => all && all.map(c => (c.chat_id === chat.chat_id ? { ...c, pinned } : c)));
    pinLookupChat(chat.chat_id, pinned, token).catch(refresh);
  }

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(false);
    try {
      await deleteLookupChat(deleting.chat_id, token);
    } catch {
      setDeleteBusy(false);
      setDeleteError(true);
      return;
    }
    const gone = deleting.chat_id;
    setChats(all => all && all.filter(c => c.chat_id !== gone));
    setDeleting(null);
    setDeleteBusy(false);
    // The open chat was deleted: open a new one in its place.
    if (gone === chatId) navigate(newChatPath(), { replace: true });
  }

  function toggleGroup(label: string) {
    setCollapsed(now => {
      const next = new Set(now);
      if (next.has(label)) next.delete(label); else next.add(label);
      try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(next))); } catch { /* private mode */ }
      return next;
    });
  }

  // Pinned chats come first, in their own group; the rest by day. The list
  // arrives newest first, so each day is one run of chats.
  // Search narrows the list by name, every group unfolded.
  const needle = query.trim().toLowerCase();
  const shown = (chats ?? []).filter(c => !needle || c.title.toLowerCase().includes(needle));
  const groups: { label: string; chats: SavedLookupChat[] }[] = [];
  const pinnedChats = shown.filter(c => c.pinned);
  if (pinnedChats.length) groups.push({ label: PINNED_LABEL, chats: pinnedChats });
  for (const chat of shown.filter(c => !c.pinned)) {
    const label = lastUsedLabel(chat.updated_at);
    if (groups[groups.length - 1]?.label !== label) groups.push({ label, chats: [] });
    groups[groups.length - 1].chats.push(chat);
  }

  const menuItem = 'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-fg hover:bg-surface-sunken focus-visible:bg-surface-sunken focus:outline-none';

  return (
    <div className="h-full min-h-0 flex flex-col bg-surface text-fg">
      {/* One header across the page: "Chat" lines up with the chat list's
          text below it (px-5 = the list's px-3 + each row's px-2). */}
      <header className="flex items-center justify-between gap-4 px-5 py-4 border-b border-border-default">
        <h1 className="text-lg font-semibold">Chat</h1>
        {/* On a narrow screen the chat list (and its New chat) is hidden. */}
        <button type="button" onClick={() => navigate(newChatPath())} className="md:hidden flex items-center gap-2 h-9 px-4 rounded-md bg-primary text-on-primary text-[13px] font-semibold hover:bg-primary-hover focus-visible:shadow-bark-focus">
          <Plus size={16} strokeWidth={1.5} aria-hidden="true" /> New chat
        </button>
      </header>
      <div className="flex-1 min-h-0 flex">
        <nav aria-label="Chats" className="hidden md:flex w-64 shrink-0 flex-col border-r border-border-default bg-surface">
          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-4">
            {/* Search and New chat stay in view while the list scrolls. */}
            <div className="sticky top-0 z-10 -mx-3 -mt-4 px-3 pt-4 pb-1 bg-surface space-y-2">
            {!!chats?.length && (
              <div className="relative">
                <Search size={14} strokeWidth={1.5} aria-hidden="true" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-tertiary" />
                <input
                  type="search"
                  aria-label="Search chats"
                  placeholder="Search chats"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setQuery(''); }}
                  className="block w-full h-8 pl-8 pr-7 text-[13px] bg-surface-sunken border border-border-default rounded-md text-fg placeholder:text-fg-tertiary focus:outline-none focus:border-accent focus:shadow-bark-focus [&::-webkit-search-cancel-button]:hidden"
                />
                {query && (
                  <button type="button" aria-label="Clear search" onClick={() => setQuery('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-fg-tertiary hover:text-fg focus-visible:shadow-bark-focus">
                    <X size={14} strokeWidth={1.5} aria-hidden="true" />
                  </button>
                )}
              </div>
            )}
            {/* New chat, under Search: a plain row that reads as the first line
                of the list it adds to. Same left edge (px-2) and size (13px) as
                the chat names; one step darker (fg-secondary) as the action. */}
            <button type="button" onClick={() => navigate(newChatPath())} className="flex w-full items-center gap-2 px-2 py-1 rounded-md text-left text-[13px] text-fg-secondary hover:bg-bg-warm hover:text-fg focus-visible:shadow-bark-focus">
              <span aria-hidden="true" className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-surface-sunken text-fg-secondary">
                <Plus size={14} strokeWidth={1.5} />
              </span>
              New chat
            </button>
            </div>
            {listError && <div role="alert" className="bg-danger-bg rounded-md p-3 text-sm">
              <p>Your chats could not be loaded.</p>
              <button type="button" className="mt-2 underline rounded focus-visible:shadow-bark-focus" onClick={refresh}>Try again</button>
            </div>}
            {chats === null && !listError && <p role="status" className="flex items-center gap-2 px-2 text-sm text-fg-secondary"><Loader2 size={16} strokeWidth={1.5} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />Loading chats</p>}
            {chats?.length === 0 && <p className="px-2 text-sm text-fg-secondary">Your chats will appear here.</p>}
            {!!chats?.length && needle && shown.length === 0 && <p className="px-2 text-[13px] text-fg-secondary">No chats match “{query.trim()}”.</p>}
            {groups.map(group => {
              const open = !!needle || !collapsed.has(group.label);
              return (
                <section key={group.label} aria-label={group.label}>
                  <h2 className="mb-1 border-b border-border-default">
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => toggleGroup(group.label)}
                      className="flex w-full items-center gap-1 px-2 pb-1 text-left text-xs font-semibold text-fg rounded focus-visible:shadow-bark-focus"
                    >
                      {open ? <ChevronDown size={14} strokeWidth={1.5} aria-hidden="true" /> : <ChevronRight size={14} strokeWidth={1.5} aria-hidden="true" />}
                      {group.label === PINNED_LABEL && <Pin size={12} strokeWidth={1.5} aria-hidden="true" />}
                      {group.label}
                    </button>
                  </h2>
                  {open && <ul className="space-y-0.5">
                    {group.chats.map(chat => (
                      <li key={chat.chat_id} className="group relative">
                        {renaming === chat.chat_id ? (
                          <input
                            autoFocus
                            aria-label="Chat name"
                            value={renameDraft}
                            onChange={e => setRenameDraft(e.target.value)}
                            onFocus={e => e.currentTarget.select()}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); endRename(chat, true); }
                              else if (e.key === 'Escape') { e.preventDefault(); endRename(chat, false); }
                            }}
                            onBlur={() => endRename(chat, true)}
                            className="block w-full px-2 py-1.5 text-[13px] bg-surface-sunken border border-border-default rounded-md text-fg focus:outline-none focus:border-accent focus:shadow-bark-focus"
                          />
                        ) : (
                          <>
                            <button
                              type="button"
                              title={chat.title}
                              aria-current={chat.chat_id === chatId ? 'page' : undefined}
                              onClick={() => navigate(`/lookup/${encodeURIComponent(chat.chat_id)}`)}
                              className={`block w-full truncate text-left pl-2 pr-8 py-1.5 rounded-md text-[13px] focus-visible:shadow-bark-focus ${chat.chat_id === chatId ? 'bg-accent-tint text-accent-hover font-semibold' : 'text-fg-tertiary hover:bg-bg-warm hover:text-fg'}`}
                            >
                              {chat.title}
                            </button>
                            <button
                              type="button"
                              aria-label={`Options for ${chat.title}`}
                              aria-haspopup="menu"
                              aria-expanded={menuFor === chat.chat_id}
                              title="Options"
                              onClick={() => setMenuFor(id => (id === chat.chat_id ? null : chat.chat_id))}
                              className={`absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-md text-fg-tertiary hover:text-fg group-hover:opacity-100 focus-visible:opacity-100 focus-visible:shadow-bark-focus ${menuFor === chat.chat_id ? 'opacity-100' : 'opacity-0'}`}
                            >
                              <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden="true" />
                            </button>
                            {menuFor === chat.chat_id && (
                              <div ref={menu} role="menu" aria-label={`Options for ${chat.title}`} className="absolute right-1 top-full z-20 mt-1 w-40 rounded-md border border-border-default bg-surface py-1 shadow-bark-md">
                                <button type="button" role="menuitem" className={menuItem} onClick={() => startRename(chat)}>
                                  <Pencil size={14} strokeWidth={1.5} aria-hidden="true" /> Rename
                                </button>
                                <button type="button" role="menuitem" className={menuItem} onClick={() => togglePin(chat)}>
                                  {chat.pinned ? <PinOff size={14} strokeWidth={1.5} aria-hidden="true" /> : <Pin size={14} strokeWidth={1.5} aria-hidden="true" />}
                                  {chat.pinned ? 'Unpin' : 'Pin'}
                                </button>
                                <button type="button" role="menuitem" className={`${menuItem} text-danger`} onClick={() => { setMenuFor(null); setDeleteError(false); setDeleting(chat); }}>
                                  <Trash2 size={14} strokeWidth={1.5} aria-hidden="true" /> Delete
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </li>
                    ))}
                  </ul>}
                </section>
              );
            })}
          </div>
        </nav>
        <div className="flex-1 min-w-0">
          {/* An article link in an answer opens the Library beside the chat
              (no Library origin configured: the links open EUR-Lex). */}
          <LibraryPanelHost key={chatId} origin={api.getLibraryOrigin()}>
            <LookupChat key={chatId} chatId={chatId} token={token} onStarted={listStarted} onAbandoned={unlistAbandoned} onSaved={(savedId, thread) => { refresh(); autoTitle(savedId, thread); }} />
          </LibraryPanelHost>
        </div>
      </div>
      <ConfirmDialog
        open={deleting !== null}
        title="Delete this chat?"
        description={deleting ? `"${deleting.title}" and all its messages will be deleted. This cannot be undone.` : undefined}
        error={deleteError ? 'This chat could not be deleted. Please try again.' : undefined}
        confirmLabel="Delete"
        variant="destructive"
        submitting={deleteBusy}
        onCancel={() => { if (!deleteBusy) { setDeleting(null); setDeleteError(false); } }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default function LookupRoute() {
  const { authToken, userData } = useAuth();
  const { chatId } = useParams();
  const identity = userData?.uid || userData?.id || userData?.email;
  if (!authToken || !identity) return <p role="status" className="p-6 text-sm text-fg-secondary">Sign in to use Chat.</p>;
  if (!chatId) return <Navigate to={newChatPath()} replace />;
  return <LookupPage key={identity} chatId={chatId} token={authToken} />;
}
